import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FiMail, FiArrowRight, FiArrowLeft, FiShield, FiCheck, FiInfo } from "react-icons/fi";
import { GoPasskeyFill } from "react-icons/go";
import { IoMdUnlock } from "react-icons/io";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";

import { NGROK } from "../../../APIs";
import UserService, { useKeycloakStore } from "../../../services/UserService";
import { myLocalStorage } from "../../../components/StorageHelper";
import { decodeKeycloakToken } from "../../../utils/tokenHelpers";
import useUserStore from "../../../services/userStore";
import useAuth from "../../../hooks/useAuth";
import AuthenticationPage from "./components/AuthenticationPage";

const AuthSignUp = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();
  const { getUserData: fetchUserData } = useUserStore((state) => state);
  const { local_kc } = useKeycloakStore();

  // Step states: 1=email, 2=OTP, 3=signup options, 4=tenant name
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [, setTenantName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [tenantError, setTenantError] = useState("");
  const [serviceTermsAgreed, setServiceTermsAgreed] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const isTenantNameFormActive =
    local_kc &&
    (local_kc.realm === "whiteswan_tenants" ||
      local_kc.realm === "whiteswan_tenants_legacy");

  // Validation schemas
  const SignupSchema = Yup.object().shape({
    email: Yup.string()
      .email("Invalid email format")
      .trim("No whitespace allowed")
      .strict(true)
      .required("Email is required"),
    tenantName: Yup.string()
      .min(5, "Too Short!")
      .max(50, "Too Long!")
      .required("Required")
      .test(
        "is-lowercase",
        "Only lowercase letters are allowed.",
        (value) => !/[A-Z]/.test(value)
      ),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      tenantName: "",
    },
    validationSchema: SignupSchema,
    onSubmit: () => { },
  });

  // API methods
  const getTenantsInfo = async (email) => {
    const axiosInstance = axios.create();
    try {
      const tenants = await axiosInstance.get(
        `${NGROK}/api/get-tenants?email=${email.toLowerCase().trim()}`
      );
      return tenants;
    } catch (error) {
      setTenantName("");
      return null;
    }
  };

  const checkIfUserExist = async (email) => {
    try {
      const tenants = await getTenantsInfo(email.toLowerCase().trim());

      if (!tenants || !tenants.data?.length) {
        setTenantName("");
        return false;
      }

      const userInfo = tenants.data[0];
      const hasValidRealm = userInfo?.realm?.realmName || userInfo?.legacyRealm?.realmName;
      if (!hasValidRealm) {
        setTenantName("");
        return false;
      }

      myLocalStorage.setItem("role", userInfo.role);
      setFirstName(userInfo.firstName);
      setLastName(userInfo.lastName);
      setTenantName(userInfo.tenantName);
      return userInfo;
    } catch (error) {
      console.error(error);
      setTenantName("");
      return false;
    }

  };

  const keycloakInitAndRegister = async (realm) => {
    try {
      setLoading(true);
      myLocalStorage.setItem("realm", realm);
      UserService.initKeycloak(realm);

      setTimeout(() => {
        UserService.doRegister(email.toLowerCase().trim(), firstName, lastName);
      }, 2000);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const verifyEmailInvite = useCallback(
    async (otp) => {
      const axiosInstance = axios.create();
      let params = {
        email: email.toLowerCase().trim(),
        verificationCode: parseInt(otp),
      };

      try {
        let { data } = await axiosInstance.post(
          `${NGROK}/api/invite/validate`,
          params
        );
        if (!data.isOk) {
          setOtpError("Invalid or expired code. Please try again.");
          return false;
        } else {
          setOtpError("");
          return true;
        }
      } catch (error) {
        console.error(error);
        setOtpError("Unable to verify code. Please try again.");
        return false;
      }
    },
    [email]
  );

  // Handlers
  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    setSubmitLoading(true);
    setEmailError("");

    const errors = await formik.validateForm();
    if (!errors?.email) {
      formik.handleSubmit();
      const user = await checkIfUserExist(email.toLowerCase().trim());


      if (
        user?.realm?.keycloakAccountExists ||
        user?.legacyRealm?.keycloakAccountExists
      ) {
        setEmailError("User with this email already exists.");
        setSubmitLoading(false);
        return;
      } else if (user.isVerificationRequired) {
        // Need OTP verification
        setStep(2);
      } else if (user) {
        // Go to signup options
        setStep(3);
      }

      setSubmitLoading(false);
    } else {
      setEmailError(errors.email);
      setSubmitLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setOtpError("");

    const isValid = await verifyEmailInvite(otp);
    if (isValid) {
      setStep(3);
    }
    setSubmitLoading(false);
  };

  const startRegisterViaPasskey = async () => {
    if (!serviceTermsAgreed) return;

    setLoading(true);
    const user = await checkIfUserExist(email.toLowerCase().trim());

    if (
      user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
      user?.realm?.keycloakAccountExists
    ) {
      setEmailError("User with this email already exists.");
      setLoading(false);
      return;
    }

    if (!user) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants");
    } else if (
      user &&
      user.isInvitedAdmin &&
      !user.realm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants");
    } else if (user && !user.realm?.keycloakAccountExists) {
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister(user.tenantName);
    } else {
      setEmailError("No invitation found for this email. Please contact your administrator.");
      setLoading(false);
    }
  };

  const startRegisterViaPassword = async () => {
    if (!serviceTermsAgreed) return;

    setLoading(true);
    const user = await checkIfUserExist(email.toLowerCase().trim());

    if (
      user?.legacyRealm?.realmName === "whiteswan_tenants_initial" ||
      user?.legacyRealm?.keycloakAccountExists
    ) {
      setEmailError("User with this email already exists.");
      setLoading(false);
      return;
    }

    if (!user) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants_legacy");
    } else if (
      user &&
      user.isInvitedAdmin &&
      !user.realm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_ADMIN");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("LastName", lastName);
      keycloakInitAndRegister("whiteswan_tenants_legacy");
    } else if (
      user &&
      !user.isInvitedAdmin &&
      !user.legacyRealm.keycloakAccountExists
    ) {
      myLocalStorage.setItem("role", "TENANT_USER");
      myLocalStorage.setItem("email", email.toLowerCase().trim());
      myLocalStorage.setItem("firstName", firstName);
      myLocalStorage.setItem("lastName", lastName);
      keycloakInitAndRegister(`${user.legacyRealm.realmName}`);
    } else {
      setEmailError("No invitation found for this email. Please contact your administrator.");
      setLoading(false);
    }
  };

  const createTenant = async () => {
    setIsSubmitted(true);
    const errors = await formik.validateForm();

    if (errors?.tenantName) {
      setTenantError(errors.tenantName);
      return;
    }

    setLoading(true);

    const { data: tenantAlreadyExist } = await axios.get(
      `${NGROK}/api/test-connection?tenantName=${formik.values.tenantName}`
    );

    if (!tenantAlreadyExist && formik.values.tenantName) {
      const {
        email: userEmail,
        family_name,
        given_name,
      } = decodeKeycloakToken(local_kc.token);

      try {
        const response = await signUp(
          formik.values.tenantName,
          userEmail,
          `${family_name} ${given_name}`
        );
        fetchUserData(userEmail);

        if (response) {
          navigate("/loading");
        } else {
          setLoading(false);
        }
      } catch (error) {
        setTenantError("Something went wrong during test connection.");
        console.error(error, "sign up error");
        setLoading(false);
      }
    }
  };

  const stringToColor = (name) => {
    let hash = 0;
    let i;

    for (i = 0; i < name.length; i += 1) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = "#";

    for (i = 0; i < 3; i += 1) {
      const value = (hash >> (i * 8)) & 0xff;
      color += `00${value.toString(16)}`.slice(-2);
    }

    return color;
  };

  const getInitials = (name) => {
    let initials = "";
    const nameParts = name.split(" ");

    if (nameParts.length >= 2) {
      initials = `${nameParts[0][0]}${nameParts[1][0]}`;
    } else if (name.includes("@")) {
      const emailParts = name.split("@")[0].split(".");
      if (emailParts.length >= 2) {
        initials = `${emailParts[0][0]}${emailParts[1][0]}`;
      } else {
        initials = `${name[0]}${name[1]}`;
      }
    } else {
      initials = `${name[0]}${name[1]}`;
    }

    return initials.toUpperCase();
  };

  const realm = myLocalStorage.getItem("realm");
  const access_token =
    UserService?.getToken() || myLocalStorage.getItem("access_token");

  useEffect(() => {
    if (!access_token && realm) {
      setLoading(true);
      setTimeout(() => {
        setLoading(false);
      }, 4000);
    }
  }, [access_token, realm]);

  if (loading) {
    return (
      <AuthenticationPage>
        <div className="auth-overlay">
          <div className="auth-spinner" data-tone="blue"></div>
          <span>Navigating to Signup page</span>
        </div>
      </AuthenticationPage>
    );
  }

  return (
    <AuthenticationPage>
      <div className="auth-card">
        <span className="auth-card-icon">
          <FiShield />
        </span>
        <h1 className="auth-title">{isTenantNameFormActive ? "Set up your tenant" : "Create your account"}</h1>
        <p className="auth-subtitle">
          {isTenantNameFormActive && "Choose the name of your Whiteswan workspace."}
          {!isTenantNameFormActive && step === 1 && "Enter the email address you were invited with."}
          {!isTenantNameFormActive && step === 2 && "Enter the six digit code from your invitation."}
          {!isTenantNameFormActive && step === 3 && "Choose how you want to sign in."}
        </p>

        {!isTenantNameFormActive && (
          <div className="auth-steps" aria-hidden="true">
            {[1, 2, 3].map((item) => (
              <span key={item} data-active={step === item} />
            ))}
          </div>
        )}

        <div className="auth-body">
          {step === 1 && !isTenantNameFormActive && (
            <>
              <form onSubmit={handleEmailSubmit}>
                <label className="auth-label">Email address</label>
                <div className="auth-input-wrap">
                  <FiMail className="auth-input-icon" />
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value.toLowerCase().trim());
                      formik.handleChange(e);
                      setEmailError("");
                      setIsSubmitted(false);
                    }}
                    placeholder="you@company.com"
                    className="auth-input"
                    required
                  />
                  <button
                    type="submit"
                    disabled={!email || submitLoading}
                    className="auth-inline-submit"
                    aria-label="Continue"
                  >
                    {submitLoading ? <span className="auth-spinner"></span> : <FiArrowRight />}
                  </button>
                </div>
                {emailError && <div className="auth-error auth-reveal">{emailError}</div>}
              </form>
              <div className="auth-note auth-reveal" data-delay="1" role="note" data-testid="signup-invite-disclaimer">
                <FiInfo aria-hidden="true" />
                <div>
                  <strong>Invitation required</strong>
                  <p>
                    Make sure this email address has been invited by your organization admin and you have the six digit
                    code before signing up.
                  </p>
                </div>
              </div>
            </>
          )}

          {step === 2 && (
            <form onSubmit={handleOtpSubmit} className="auth-body auth-reveal" style={{ marginTop: 0 }}>
              <div>
                <button type="button" onClick={() => setStep(1)} className="auth-back">
                  <span>
                    <FiArrowLeft />
                  </span>
                  <span>{email}</span>
                </button>
              </div>
              <div>
                <label className="auth-label">Verification code</label>
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    setOtpError("");
                  }}
                  maxLength={6}
                  placeholder="000000"
                  className="auth-input"
                  data-code="true"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  required
                />
                {otpError && <div className="auth-error auth-reveal">{otpError}</div>}
              </div>
              <button type="submit" disabled={!otp || submitLoading} className="auth-primary">
                {submitLoading ? "Verifying..." : "Verify code"}
              </button>
            </form>
          )}

          {step === 3 && !isTenantNameFormActive && (
            <>
              <div className="auth-identity auth-reveal">
                <span className="auth-avatar" style={{ backgroundColor: stringToColor(email) }}>
                  {getInitials(email)}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p>
                    Hello, {firstName} {lastName}
                  </p>
                  <p>{email}</p>
                </div>
              </div>

              <button
                type="button"
                onClick={startRegisterViaPasskey}
                disabled={!serviceTermsAgreed || loading}
                className="auth-primary auth-reveal"
                data-delay="1"
              >
                <GoPasskeyFill />
                Sign up with Passkey
              </button>

              <div className="auth-divider auth-reveal" data-delay="1">OR</div>

              <button
                type="button"
                onClick={startRegisterViaPassword}
                disabled={!serviceTermsAgreed || loading}
                className="auth-secondary auth-reveal"
                data-delay="2"
              >
                <IoMdUnlock />
                Sign up with Password & OTP
              </button>

              <label className="auth-check auth-reveal" data-delay="3">
                <input
                  type="checkbox"
                  checked={serviceTermsAgreed}
                  onChange={(e) => setServiceTermsAgreed(e.target.checked)}
                  className="sr-only"
                />
                <span className="auth-check-box" data-checked={serviceTermsAgreed}>
                  {serviceTermsAgreed && <FiCheck strokeWidth={3} size={12} />}
                </span>
                <span>
                  I agree to the{" "}
                  <a href="/terms_of_service" className="auth-link">
                    Terms and Conditions
                  </a>
                </span>
              </label>
            </>
          )}

          {isTenantNameFormActive && (
            <div className="auth-reveal">
              <label className="auth-label">Tenant name</label>
              <div className="auth-input-wrap">
                <input
                  type="text"
                  name="tenantName"
                  value={formik.values.tenantName}
                  onChange={(e) => {
                    formik.handleChange(e);
                    setTenantError("");
                    setIsSubmitted(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      createTenant();
                    }
                  }}
                  placeholder="your-company"
                  className="auth-input"
                  style={{ paddingLeft: 14 }}
                  data-lowercase="true"
                  required
                />
                <button
                  type="button"
                  onClick={createTenant}
                  disabled={!formik.values.tenantName || submitLoading}
                  className="auth-inline-submit"
                  aria-label="Create tenant"
                >
                  {submitLoading ? <span className="auth-spinner"></span> : <FiArrowRight />}
                </button>
              </div>
              {tenantError && isSubmitted && <div className="auth-error auth-reveal">{tenantError}</div>}
              <p className="auth-hint">Must be 5-50 characters, lowercase letters only.</p>
            </div>
          )}
        </div>

        <div className="auth-footer">
          <span>
            Already have an account?{" "}
            <button type="button" onClick={() => navigate("/signIn")} className="auth-link">
              Sign in
            </button>
          </span>
        </div>
      </div>
    </AuthenticationPage>
  );
};

export default AuthSignUp;
